import {
  BarcodeOutlined,
  BoxPlotOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DatabaseOutlined,
  EditOutlined,
  EnvironmentOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Html } from "@react-three/drei";
import { Button, Card, Descriptions, Divider, Form, Input, message, Space, Tag, Typography } from "antd";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Store } from "../../../../store";
import { updateBin } from "../../services/binService";
import "./BinDetailModal.css";

const { Text, Title } = Typography;
const { Item } = Form;

const BinDetailModal = ({ binData, onClose, onBinUpdate }) => {
  const { state } = useContext(Store);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentBinData, setCurrentBinData] = useState(binData);
  const modalRef = useRef(null);

  // Update current bin data when prop changes
  useEffect(() => {
    if (binData) {
      setCurrentBinData(binData);

      // If editing mode is active and binData changes, exit edit mode
      if (isEditing) {
        setIsEditing(false);
        form.resetFields();
      }

      // Reset form with new data
      form.setFieldsValue({
        name: binData.name || "",
        code: binData.code || "",
      });
    }
  }, [binData, form]); // Removed isEditing from dependency array to prevent circular updates

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (isEditing) {
          handleCancelEdit();
        } else {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscape, true);
    return () => {
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [isEditing]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add with capture phase
    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("touchstart", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [onClose]);

  // Reset form when entering/exiting edit mode
  useEffect(() => {
    if (currentBinData) {
      form.setFieldsValue({
        name: currentBinData.name || "",
        code: currentBinData.code || "",
      });
    }
  }, [isEditing, currentBinData, form]);

  const stopEventPropagation = useCallback((e) => {
    e.stopPropagation();
    e.nativeEvent?.stopImmediatePropagation?.();
  }, []);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    form.setFieldsValue({
      name: currentBinData?.name || "",
      code: currentBinData?.code || "",
    });
  }, [currentBinData, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // API Payload must match backend structure
      const payload = {
        rackId: currentBinData?.rackId,
        binId: currentBinData?.id,
        binCode: values.code,
        binName: values.name,
      };

      const response = await updateBin(payload.binId, payload);

      // Create updated bin data
      const updatedBinData = {
        ...currentBinData,
        code: values.code,
        name: values.name,
      };

      // Update local state
      setCurrentBinData(updatedBinData);

      // Notify parent component
      if (onBinUpdate) {
        onBinUpdate(updatedBinData);
      }

      message.success("Bin updated successfully!");
      setIsEditing(false);
    } catch (error) {
      if (error.errorFields) {
        message.error("Please fill in all required fields correctly");
      } else {
        message.error("Failed to update bin");
        console.error("Update error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Memoized utility functions
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const calculateArea = useCallback(() => {
    if (currentBinData?.width && currentBinData?.height) {
      return (currentBinData.width * currentBinData.height).toFixed(2);
    }
    return "N/A";
  }, [currentBinData]);

  const getRackName = useCallback(() => {
    if (currentBinData?.rackId) {
      const { elements } = state;
      const rack = elements.find((element) => element.id === currentBinData?.rackId);
      return rack?.name || "N/A";
    }
    return "N/A";
  }, [currentBinData, state]);

  if (!currentBinData) return null;

  return (
    <Html fullscreen>
      <div
        ref={modalRef}
        style={{
          position: "absolute",
          left: 20,
          top: 20,
          pointerEvents: "auto",
          zIndex: 1000,
        }}
      >
        <Card
          style={{
            width: 320,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            border: "1px solid #d9d9d9",
            borderRadius: 12,
            background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
          }}
          bodyStyle={{ padding: 16 }}
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Space>
                <DatabaseOutlined style={{ color: "#1890ff" }} />
                <Title level={5} style={{ margin: 0 }}>
                  {isEditing ? "Edit Bin" : "Bin Details"}
                </Title>
              </Space>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={isEditing ? handleCancelEdit : onClose}
                size="small"
                style={{ color: "#8c8c8c" }}
                onMouseDown={stopEventPropagation}
              />
            </div>
          }
        >
          <Form form={form} layout="vertical">
            {/* Basic Information */}
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item
                label={
                  <Space size="small">
                    <BarcodeOutlined />
                    <Text strong>Code</Text>
                  </Space>
                }
              >
                {isEditing ? (
                  <Item name="code" rules={[{ required: true, message: "Please enter bin code!" }]} style={{ margin: 0 }}>
                    <Input prefix={<BarcodeOutlined />} placeholder="Enter bin code" size="small" onMouseDown={stopEventPropagation} autoComplete="off" />
                  </Item>
                ) : (
                  <Tag color="blue" icon={<BarcodeOutlined />}>
                    {currentBinData.code || "N/A"}
                  </Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <Space size="small">
                    <BoxPlotOutlined />
                    <Text strong>Name</Text>
                  </Space>
                }
              >
                {isEditing ? (
                  <Item name="name" rules={[{ required: true, message: "Please enter bin name!" }]} style={{ margin: 0 }}>
                    <Input prefix={<BoxPlotOutlined />} placeholder="Enter bin name" size="small" onMouseDown={stopEventPropagation} autoComplete="off" />
                  </Item>
                ) : (
                  currentBinData.name || "N/A"
                )}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <Space size="small">
                    <EnvironmentOutlined />
                    <Text strong>Rack</Text>
                  </Space>
                }
              >
                {getRackName()}
              </Descriptions.Item>
            </Descriptions>

            {!isEditing && (
              <>
                <Divider style={{ margin: "12px 0" }} />

                {/* Dimensions */}
                <Title level={5} style={{ marginBottom: 12 }}>
                  <BoxPlotOutlined /> Dimensions
                </Title>
                <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Width">
                    {currentBinData.width ? `${currentBinData.width} ${currentBinData.unitType || ""}` : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Height">
                    {currentBinData.height ? `${currentBinData.height} ${currentBinData.unitType || ""}` : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Position">
                    X: {currentBinData.x || "N/A"}, Y: {currentBinData.y || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Area">
                    {calculateArea()} {currentBinData.unitType ? `${currentBinData.unitType}` : ""}
                    {currentBinData.unitType ? <sup>2</sup> : ""}
                  </Descriptions.Item>
                </Descriptions>

                {/* Coordinates - only show if available */}
                {(currentBinData.x1 !== undefined || currentBinData.y1 !== undefined) && (
                  <>
                    <Title level={5} style={{ marginBottom: 12 }}>
                      <EnvironmentOutlined /> Coordinates
                    </Title>
                    <Space direction="vertical" size="small" style={{ width: "100%", marginBottom: 16 }}>
                      {currentBinData.x1 !== undefined && currentBinData.x2 !== undefined && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <Text type="secondary">X Range:</Text>
                          <Text>
                            {currentBinData.x1} - {currentBinData.x2}
                          </Text>
                        </div>
                      )}
                      {currentBinData.y1 !== undefined && currentBinData.y2 !== undefined && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <Text type="secondary">Y Range:</Text>
                          <Text>
                            {currentBinData.y1} - {currentBinData.y2}
                          </Text>
                        </div>
                      )}
                    </Space>
                  </>
                )}

                <Divider style={{ margin: "12px 0" }} />

                {/* Metadata */}
                <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item
                    label={
                      <Space size="small">
                        <CalendarOutlined />
                        <Text strong>Created</Text>
                      </Space>
                    }
                  >
                    {formatDate(currentBinData.createdAt)}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space size="small">
                        <CalendarOutlined />
                        <Text strong>Updated</Text>
                      </Space>
                    }
                  >
                    {formatDate(currentBinData.updatedAt)}
                  </Descriptions.Item>
                </Descriptions>

                {/* Color Indicator */}
                {currentBinData.color && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Color:
                    </Text>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: currentBinData.color,
                          borderRadius: 4,
                          border: "1px solid #d9d9d9",
                        }}
                      />
                      <Text>{currentBinData.color}</Text>
                    </div>
                  </div>
                )}

                {/* Labels */}
                {currentBinData.labels && currentBinData.labels.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Labels:
                    </Text>
                    <Space wrap>
                      {currentBinData.labels.map((label, index) => (
                        <Tag key={index} color="default">
                          {label}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
              </>
            )}

            {/* Action Buttons */}
            <Divider style={{ margin: "12px 0" }} />
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              {isEditing ? (
                <>
                  <Button icon={<CloseCircleOutlined />} onClick={handleCancelEdit} size="small" disabled={loading} onMouseDown={stopEventPropagation}>
                    Cancel
                  </Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} size="small" loading={loading} onMouseDown={stopEventPropagation}>
                    Save
                  </Button>
                </>
              ) : (
                <Button type="primary" icon={<EditOutlined />} size="small" onClick={handleEdit} onMouseDown={stopEventPropagation}>
                  Edit
                </Button>
              )}
            </Space>
          </Form>
        </Card>
      </div>
    </Html>
  );
};

export default BinDetailModal;
